import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DateTimePicker } from '@nucleus-commerce/admin-kit/date-time';
import { fromZonedInput, toZonedInput } from '@nucleus-commerce/core';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { useStageFormError } from '../hooks/use-stage-form-error';
import { useUpdateStage } from '../hooks/use-workflow-mutations';
import { dueDateSchema, type DueDateForm } from '../schemas/stage-forms';

import { ActionDialog } from './action-dialog';
import type { StageTarget } from './stage-target';

const KNOWN = new Set(['dueAt']);

/**
 * An exact due time on the store's wall clock (2026-10-02 13:00 in New York), sent as a UTC
 * instant. The time zone comes from the store the page is formatted in.
 */
export function DueDateDialog({
  target,
  onOpenChange,
}: {
  target: StageTarget;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation('production');
  const format = useFormat();
  const update = useUpdateStage(target.storeId, target.orderId, 'production:toasts.dueSet');
  const form = useForm<DueDateForm>({
    resolver: useI18nResolver(dueDateSchema),
    defaultValues: { dueAt: toZonedInput(target.run.dueAt, format.timeZone) },
  });
  const onError = useStageFormError(form.setError, { knownFields: KNOWN });

  const save = async (dueAt: string | null) => {
    form.clearErrors('root.server');
    try {
      await update.mutateAsync({ key: target.key, body: { dueAt } });
      onOpenChange(false);
    } catch (error) {
      onError(error);
    }
  };

  const submit = form.handleSubmit(async (values) => {
    const iso = fromZonedInput(values.dueAt, format.timeZone);
    if (!iso) {
      form.setError('dueAt', { type: 'validate', message: t('validation.due') });
      return;
    }
    await save(iso);
  });

  return (
    <Form {...form}>
      <ActionDialog
        open
        onOpenChange={onOpenChange}
        title={t('due.title', { name: target.name })}
        description={t('due.description', { zone: format.timeZone })}
        rootError={form.formState.errors.root?.server?.message}
        isPending={update.isPending}
        submitLabel={t('due.submit')}
        submittingLabel={t('due.submitting')}
        onSubmit={submit}
      >
        <FormField
          control={form.control}
          name="dueAt"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('due.label')}</FormLabel>
              <FormControl>
                <DateTimePicker {...field} timeZone={format.timeZone} className="sm:max-w-64" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {target.run.dueAt ? (
          <Button
            type="button"
            variant="link"
            className="self-start px-0"
            disabled={update.isPending}
            onClick={() => void save(null)}
          >
            {t('due.clear')}
          </Button>
        ) : null}
      </ActionDialog>
    </Form>
  );
}
